// Wall panels that belong to a LOCATION, on fixed button grids.
//
// ── Why the location lives in the name ─────────────────────────────────────
// WallPanel has WallPanelId, UserId, PanelName, SortOrder, BackColor,
// ColumnsCount, IsActive — and NO LocationID. A wall panel for a site therefore
// has nowhere on the row to record which site it is.
//
// The same problem was solved once already: per-room Scenes live on these very
// tables behind a name prefix (SCENE_PANEL_PREFIX). This follows that
// precedent rather than inventing a second convention, and needs no backend
// change — which matters, because the deployed API is ahead of the local copy
// and cannot be redeployed from here.
//
//     PanelName = "#LOC:<locationId>:<display name>"
//
// A panel without the prefix is an ordinary personal panel and is left alone.
// ⚠️ Anything reading panels must therefore FILTER, not assume: a user's panel
// list mixes personal panels, scene panels and location panels.

export const LOCATION_PANEL_PREFIX = '#LOC:';

/** "#LOC:29:Front Room" -> { locationId: 29, name: 'Front Room' } | null */
export function parseLocationPanel(panelName) {
  const text = String(panelName || '');
  if (!text.startsWith(LOCATION_PANEL_PREFIX)) return null;
  const rest = text.slice(LOCATION_PANEL_PREFIX.length);
  const colon = rest.indexOf(':');
  if (colon <= 0) return null;
  const locationId = Number(rest.slice(0, colon));
  if (!Number.isFinite(locationId)) return null;
  return { locationId, name: rest.slice(colon + 1) };
}

export const buildLocationPanelName = (locationId, name) =>
  `${LOCATION_PANEL_PREFIX}${Number(locationId)}:${String(name || '').trim()}`;

export const isLocationPanel = (panel) => parseLocationPanel(panel?.PanelName) !== null;

/** The panels belonging to one location, in sort order. */
export const panelsForLocation = (panels, locationId) => (panels || [])
  .filter((p) => {
    const parsed = parseLocationPanel(p.PanelName);
    return parsed && String(parsed.locationId) === String(locationId);
  })
  .sort((a, b) => (Number(a.SortOrder) || 0) - (Number(b.SortOrder) || 0));

// ── Fixed grids ────────────────────────────────────────────────────────────
// A wall panel is a fixed sheet of buttons, not a free canvas: the tablet is
// screwed to a wall and every button must be reachable without scrolling. So
// the size is chosen from a list, and the grid is derived from it.
//
// Column counts are picked to keep tiles close to square on a landscape tablet
// — 24 buttons as 6x4 rather than 12x2, which would be a strip of slivers.
export const GRID_SIZES = [4, 6, 8, 12, 16, 24];

const COLUMNS = {
  4: 2, 6: 3, 8: 4, 12: 4, 16: 4, 24: 6,
};

export const columnsFor = (size) => COLUMNS[Number(size)] || 4;
export const rowsFor = (size) => Math.ceil(Number(size) / columnsFor(size));

/** The nearest supported size — a stored ColumnsCount may predate this list. */
export function normaliseSize(size) {
  const n = Number(size) || 0;
  if (GRID_SIZES.includes(n)) return n;
  return GRID_SIZES.reduce((best, s) => (Math.abs(s - n) < Math.abs(best - n) ? s : best), 4);
}

/**
 * Slot index -> { x, y } for a grid of `size`.
 *
 * Buttons are stored with PosX/PosY (the free-canvas fields the table already
 * has), so a fixed grid is expressed IN those coordinates rather than needing a
 * new column. Row-major: slot 0 is top-left, and reading order matches how
 * someone names buttons.
 */
export function slotToPos(index, size) {
  const cols = columnsFor(size);
  return { x: index % cols, y: Math.floor(index / cols) };
}

export function posToSlot(x, y, size) {
  const cols = columnsFor(size);
  return (Number(y) || 0) * cols + (Number(x) || 0);
}

/**
 * The buttons of a panel arranged into `size` slots, with empty slots as null.
 *
 * Slots beyond the grid are DROPPED rather than hidden off-screen: shrinking a
 * panel from 24 to 8 must not leave sixteen buttons that still fire but cannot
 * be seen or removed.
 */
export function layoutButtons(buttons, size) {
  const slots = new Array(Number(size)).fill(null);
  (buttons || []).forEach((b) => {
    const i = posToSlot(b.PosX, b.PosY, size);
    if (i >= 0 && i < slots.length && !slots[i]) slots[i] = b;
  });
  return slots;
}
